import { useEffect, useState } from "react";
import { Target, Settings2, AlertTriangle, Loader2, Save, Ban, Users, Euro, TrendingUp, Wallet } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

const MISSION_LABELS = {
  share: "Partage quotidien",
  signup: "Inscription parrainée",
  first_transaction: "1ʳᵉ transaction confirmée",
  qualified_referral: "Recommandation qualifiée (KYC)",
};

const STATUS_BADGE = {
  available: "bg-emerald-50 text-emerald-700",
  pending: "bg-amber-50 text-amber-700",
  validated: "bg-sky-50 text-sky-700",
  cancelled: "bg-rose-50 text-rose-700",
};

function eur(n) {
  return `${Number(n || 0).toFixed(2)} €`;
}

export default function AdminMissions() {
  const [tab, setTab] = useState("stats");
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [savingCfg, setSavingCfg] = useState(false);
  const [entries, setEntries] = useState([]);
  const [filterStatus, setFilterStatus] = useState("");
  const [filterType, setFilterType] = useState("");

  const loadStats = async () => {
    const r = await api.get("/admin/missions/stats");
    setStats(r.data);
  };
  const loadCfg = async () => {
    const r = await api.get("/admin/missions/config");
    setCfg(r.data);
  };
  const loadEntries = async () => {
    const params = {};
    if (filterStatus) params.status = filterStatus;
    if (filterType) params.mission_type = filterType;
    const r = await api.get("/admin/missions/entries", { params });
    setEntries(r.data || []);
  };

  const refresh = async () => {
    setLoading(true);
    try {
      await Promise.all([loadStats(), loadCfg()]);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
    setLoading(false);
  };

  useEffect(() => { refresh(); }, []);
  useEffect(() => { if (tab === "ledger") loadEntries().catch(() => {}); /* eslint-disable-next-line */ }, [tab, filterStatus, filterType]);

  const saveCfg = async () => {
    setSavingCfg(true);
    try {
      const payload = {
        enabled: cfg.enabled,
        daily_cap_eur: Number(cfg.daily_cap_eur),
        budget_total_eur: Number(cfg.budget_total_eur),
        first_tx_min_xof: Number(cfg.first_tx_min_xof),
        rewards: {
          share: Number(cfg.rewards.share),
          signup: Number(cfg.rewards.signup),
          first_transaction: Number(cfg.rewards.first_transaction),
          qualified_referral: Number(cfg.rewards.qualified_referral),
        },
      };
      const r = await api.patch("/admin/missions/config", payload);
      setCfg(r.data);
      toast.success("Configuration enregistrée");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
    setSavingCfg(false);
  };

  const cancelEntry = async (id) => {
    try {
      await api.post(`/admin/missions/entries/${id}/cancel`);
      toast.success("Récompense annulée");
      await Promise.all([loadEntries(), loadStats()]);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  if (loading) {
    return <div className="grid place-items-center py-20" data-testid="admin-missions-loading"><Loader2 className="animate-spin text-[var(--sl-primary)]" /></div>;
  }

  return (
    <div data-testid="admin-missions-page">
      <div className="flex items-center gap-3 mb-6">
        <span className="w-10 h-10 rounded-xl bg-[var(--sl-primary)]/10 grid place-items-center text-[var(--sl-primary)]"><Target size={18} /></span>
        <div>
          <h1 className="font-heading text-2xl font-bold">Missions ambassadeurs</h1>
          <p className="text-sm text-gray-500">Programme de récompenses quotidiennes (plafond serveur).</p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-5">
        <TabBtn active={tab === "stats"} onClick={() => setTab("stats")} data-testid="tab-mstats">Statistiques</TabBtn>
        <TabBtn active={tab === "config"} onClick={() => setTab("config")} data-testid="tab-mconfig">Règles & budget</TabBtn>
        <TabBtn active={tab === "ledger"} onClick={() => setTab("ledger")} data-testid="tab-mledger">Journal & anomalies</TabBtn>
      </div>

      {tab === "stats" && stats && (
        <div className="space-y-5" data-testid="admin-missions-stats">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Stat icon={Euro} label="Crédité (cumul)" value={eur(stats.total_awarded_eur)} testid="stat-lifetime" />
            <Stat icon={Wallet} label="Disponible" value={eur(stats.by_status?.available)} testid="stat-available" />
            <Stat icon={TrendingUp} label="Aujourd'hui" value={eur(stats.today_eur)} testid="stat-today" />
            <Stat icon={Users} label="Ambassadeurs actifs" value={stats.active_users || 0} testid="stat-users" />
          </div>

          <div className="rounded-2xl border bg-white p-5">
            <h3 className="font-semibold mb-3">Récompenses par mission</h3>
            <div className="grid sm:grid-cols-2 gap-3">
              {Object.entries(stats.by_mission || {}).map(([k, v]) => (
                <div key={k} className="flex items-center justify-between rounded-xl border px-4 py-3" data-testid={`bymission-${k}`}>
                  <span className="text-sm font-medium">{MISSION_LABELS[k] || k}</span>
                  <span className="text-sm text-gray-600">{v.count} entrées · {eur(v.amount_eur)} cumul</span>
                </div>
              ))}
            </div>
          </div>

          {stats.budget_total_eur !== undefined && (
            <div className="rounded-2xl border bg-white p-5" data-testid="budget-card">
              <h3 className="font-semibold mb-2">Budget du programme</h3>
              {Number(stats.budget_total_eur) > 0 ? (
                <>
                  <div className="flex justify-between text-sm mb-1">
                    <span>Dépensé {eur(stats.budget_spent_eur)}</span>
                    <span>Plafond {eur(stats.budget_total_eur)}</span>
                  </div>
                  <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
                    <div className="h-full bg-[var(--sl-primary)]" style={{ width: `${Math.min(100, (stats.budget_spent_eur / stats.budget_total_eur) * 100)}%` }} />
                  </div>
                </>
              ) : (
                <p className="text-sm text-gray-500">Budget illimité (aucun plafond global défini).</p>
              )}
            </div>
          )}

          {stats.anomalies?.length > 0 && (
            <div className="rounded-2xl border border-amber-200 bg-amber-50/50 p-5" data-testid="anomalies-card">
              <h3 className="font-semibold mb-3 flex items-center gap-2 text-amber-800"><AlertTriangle size={16} /> Anomalies détectées</h3>
              <div className="space-y-2">
                {stats.anomalies.map((a, i) => (
                  <div key={i} className="text-sm text-amber-900" data-testid={`anomaly-${i}`}>
                    {a.email || a.user_id} — {a.cap_days} jours au plafond quotidien
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {tab === "config" && cfg && (
        <div className="max-w-lg space-y-5" data-testid="admin-missions-config">
          <div className="rounded-2xl border bg-white p-5 space-y-4">
            <label className="flex items-center justify-between cursor-pointer">
              <span className="font-medium flex items-center gap-2"><Settings2 size={16} /> Programme actif</span>
              <input type="checkbox" checked={!!cfg.enabled} onChange={(e) => setCfg({ ...cfg, enabled: e.target.checked })} data-testid="cfg-enabled" className="w-5 h-5 accent-[var(--sl-primary)]" />
            </label>

            <Field label="Plafond quotidien / utilisateur (€)" value={cfg.daily_cap_eur} onChange={(v) => setCfg({ ...cfg, daily_cap_eur: v })} testid="cfg-cap" />
            <Field label="Budget global total (€, 0 = illimité)" value={cfg.budget_total_eur} onChange={(v) => setCfg({ ...cfg, budget_total_eur: v })} testid="cfg-budget" />
            <Field label="Montant min. transfert filleul (FCFA)" value={cfg.first_tx_min_xof} onChange={(v) => setCfg({ ...cfg, first_tx_min_xof: v })} testid="cfg-first-tx-min" />

            <div className="pt-2 border-t">
              <p className="text-sm font-semibold mb-2">Montants par mission (€)</p>
              {Object.keys(MISSION_LABELS).map((k) => (
                <Field key={k} label={MISSION_LABELS[k]} value={cfg.rewards?.[k]} onChange={(v) => setCfg({ ...cfg, rewards: { ...cfg.rewards, [k]: v } })} testid={`cfg-reward-${k}`} />
              ))}
            </div>

            <button onClick={saveCfg} disabled={savingCfg} className="w-full rounded-xl bg-[var(--sl-primary)] text-white py-3 font-semibold flex items-center justify-center gap-2 disabled:opacity-60" data-testid="cfg-save">
              {savingCfg ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Enregistrer
            </button>
          </div>
          <p className="text-xs text-gray-400">Réinitialisation quotidienne : Africa/Libreville (UTC+1). Toutes les limites sont appliquées côté serveur.</p>
        </div>
      )}

      {tab === "ledger" && (
        <div className="space-y-4" data-testid="admin-missions-ledger">
          <div className="flex flex-wrap gap-2">
            <select value={filterType} onChange={(e) => setFilterType(e.target.value)} className="rounded-xl border px-3 py-2 text-sm" data-testid="filter-type">
              <option value="">Toutes missions</option>
              {Object.keys(MISSION_LABELS).map((k) => <option key={k} value={k}>{MISSION_LABELS[k]}</option>)}
            </select>
            <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="rounded-xl border px-3 py-2 text-sm" data-testid="filter-status">
              <option value="">Tous statuts</option>
              <option value="available">Disponible</option>
              <option value="pending">En attente</option>
              <option value="cancelled">Annulé</option>
            </select>
          </div>

          <div className="rounded-2xl border bg-white overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-500 text-left">
                <tr>
                  <th className="px-4 py-3">Date</th>
                  <th className="px-4 py-3">Utilisateur</th>
                  <th className="px-4 py-3">Mission</th>
                  <th className="px-4 py-3">Montant</th>
                  <th className="px-4 py-3">Statut</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {entries.length === 0 && (
                  <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-400">Aucune entrée.</td></tr>
                )}
                {entries.map((e) => (
                  <tr key={e.id} className="border-t" data-testid={`entry-${e.id}`}>
                    <td className="px-4 py-3 whitespace-nowrap">{e.date_str}</td>
                    <td className="px-4 py-3 font-mono text-xs">{e.user_id?.slice(-8)}</td>
                    <td className="px-4 py-3">{MISSION_LABELS[e.mission_type] || e.mission_type}</td>
                    <td className="px-4 py-3">{eur(e.amount_eur)}</td>
                    <td className="px-4 py-3"><span className={`px-2 py-1 rounded-full text-xs font-medium ${STATUS_BADGE[e.status] || "bg-gray-100"}`}>{e.status}</span></td>
                    <td className="px-4 py-3 text-right">
                      {e.status !== "cancelled" && (
                        <button onClick={() => cancelEntry(e.id)} className="text-rose-600 hover:text-rose-700 inline-flex items-center gap-1 text-xs font-medium" data-testid={`cancel-${e.id}`}>
                          <Ban size={13} /> Annuler
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function TabBtn({ active, onClick, children, ...rest }) {
  return (
    <button onClick={onClick} {...rest} className={`px-4 py-2 rounded-xl text-sm font-medium transition ${active ? "bg-[var(--sl-primary)] text-white" : "bg-white border text-gray-600 hover:bg-gray-50"}`}>
      {children}
    </button>
  );
}

function Stat({ icon: Icon, label, value, testid }) {
  return (
    <div className="rounded-2xl border bg-white p-4" data-testid={testid}>
      <div className="flex items-center gap-2 text-gray-400 mb-1"><Icon size={15} /><span className="text-xs">{label}</span></div>
      <div className="text-lg font-bold">{value}</div>
    </div>
  );
}

function Field({ label, value, onChange, testid }) {
  return (
    <label className="block">
      <span className="text-sm text-gray-600">{label}</span>
      <input type="number" step="0.01" min="0" value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="mt-1 w-full rounded-xl border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--sl-primary)]/30" />
    </label>
  );
}
